import { apiGet, apiPost } from './client';

/**
 * Slug for the legacy single-wiki layout. Used by api/wiki.ts / api/toc.ts / etc. for now to keep
 * step-7 minimal. A future step can drop this once every consumer reads the project list dynamically.
 */
export const PRODUCT_WIKI_SLUG = 'product-wiki';

export type ProjectType = 'wiki' | 'static' | 'proxy';

export type UpdateStatus = 'ok' | 'skipped' | 'error' | 'interrupted';

export interface LastUpdate {
  ts: string;
  status: UpdateStatus;
}

export interface ProjectTile {
  id: string;
  slug: string;
  name: string;
  description: string;
  type: ProjectType;
  /** Latest UpdateEntry for this project, or null when it has never produced an entry. */
  lastUpdate: LastUpdate | null;
}

export type UpdatePhase = 'drive_ingest' | 'wiki_ingest' | 'scheduler' | 'script_refresh';

/** Full UpdateEntry exposed via the admin details endpoint. */
export interface UpdateEntry {
  ts: string;
  phase: UpdatePhase;
  status: UpdateStatus;
  summary: string;
  counts?: Record<string, number>;
  error?: string | null;
}

export interface LatestUpdateResponse {
  entry: UpdateEntry;
}

/** Fetch the projects visible to the current user (filtered by access rule + enabled flag). */
export async function getProjects(): Promise<ProjectTile[]> {
  return apiGet<ProjectTile[]>('/projects');
}

/** Admin-only: fetch the latest UpdateEntry for one project. 404 when the project has no entries yet. */
export async function getLatestUpdate(projectId: string): Promise<LatestUpdateResponse> {
  return apiGet<LatestUpdateResponse>(`/projects/${encodeURIComponent(projectId)}/updates/latest`);
}

/** Admin-only: trigger a manual refresh out of band of the schedule. Server returns 202 immediately. */
export async function triggerRefresh(projectId: string): Promise<{ triggered: boolean }> {
  return apiPost<{ triggered: boolean }, Record<string, never>>(
    `/projects/${encodeURIComponent(projectId)}/refresh`,
    {},
  );
}
